import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Badge } from "react-bootstrap";
import './CustomTagsInput.css';
import UserInfoContext from "../../../utis/userInfoContext";
import { emailRegex } from "../../../hooks/utils";

const CustomTagsInput = ({
  tags,
  handleAddTags = () => {},
  handleRemoveTags = () => {},
  triggerKeyDownEvent = () => {},
  autoSubmit = false,
  placeholderText = null,
  limit = null,
  targetKey = "name",
  isEmail = false,
  autoFocus = false,
  wrapperClassName = "",
  tagsPosition = "below",
  collapsedWhenHasTags = true,
  useTagPrimaryStyle = false, // New prop for KDF listing page styling
  onShowNotification = null, // Optional: use when parent controls toast (e.g. editor page uses EditorInfoContext)
}) => {
  const [tag, setTags] = useState(tags || []);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(() => {
    const hasTags = (tags?.length ?? 0) > 0;
    if (!collapsedWhenHasTags) return true;
    if (autoFocus) return true;
    return !hasTags; // show input when there are no tags
  });
  const { setShowNotifications } = useContext(UserInfoContext);

  const showNotification = (opts) => {
    const payload = { showToast: true, toastType: opts.toastType ?? "danger", toastMessage: opts.toastMessage ?? "" };
    if (typeof onShowNotification === "function") {
      onShowNotification(payload);
    } else {
      setShowNotifications(payload);
    }
  };

  const componentRef = useRef(null);
  const eventAddedRef = useRef(false);
  const tagInputRef = useRef(null);
  const prevTagsRef = useRef(tags || []);
  const [tagsToHighlight, setTagsToHighlight] = useState(new Set());

  // Normalize tag value for duplicate check: trim + case-insensitive
  const normalizeTagValue = (val) => {
    if (val == null) return "";
    const s = typeof val === "string" ? val : String(val?.name ?? val?.[targetKey] ?? val?.email ?? val);
    return s.trim().toLowerCase();
  };

  const addTag = (value = null) => {
    const rawValue = value ?? inputValue;
    const trimmedValue = typeof rawValue === "string" ? rawValue.trim() : rawValue;

    if (trimmedValue === "" || trimmedValue === null || trimmedValue === undefined) {
      setInputError(false);
      setHighlightedIndex(null);
      return;
    }

    if (limit != null && (tag?.length ?? 0) >= limit) {
      setInputError(true);
      showNotification({ toastMessage: `You can not add more than ${limit} tags here.` });
      return;
    }

    const existingTags = Array.isArray(tag) ? tag : [];
    const normalizedInput = normalizeTagValue(trimmedValue);

    const tagAlreadyPresent = existingTags.some((item) => {
      const existingNormalized = normalizeTagValue(
        typeof item === "string" ? item : item?.email ?? item?.[targetKey] ?? item?.name ?? item
      );
      return existingNormalized !== "" && existingNormalized === normalizedInput;
    });

    if (trimmedValue && tagAlreadyPresent) {
      showNotification({
        toastMessage: isEmail ? "This email already exists" : "This tag already exists",
      });
      setInputError(true);
      return;
    }

    if (trimmedValue && !tagAlreadyPresent) {
      if (isEmail) {
        if (emailRegex.test(trimmedValue)) {
          setTags([...existingTags, trimmedValue]);
          handleAddTags(trimmedValue);
        } else {
          showNotification({ toastMessage: "Invalid email" })
        }
      } else {
        setTags([...existingTags, trimmedValue]);
        handleAddTags(trimmedValue);
      }
    }
    setInputValue("");
    setInputError(false);
    setHighlightedIndex(null);
  };

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === "Tab" || e.key === ",") && autoSubmit == false) {
      e.preventDefault();
      addTag(e.target.value);
      // Move focus out after state updates so handleBlur sees cleared input and doesn't double-add
      const inputEl = tagInputRef.current;
      if (inputEl) setTimeout(() => inputEl.blur(), 0);
    }

    if (e.key === "Backspace" && inputValue === "") {
      e.preventDefault();
      if (highlightedIndex === null) {
        setHighlightedIndex(tag?.length - 1);
      } else if (highlightedIndex !== null) {
        const tagToRemove = tag[highlightedIndex];
        removeTag(tagToRemove);
        setHighlightedIndex(null);
      }
    }
  };

  const removeTag = (tagToRemove) => {
    const currentTags = Array.isArray(tag) ? tag : [];
    const updatedTags = currentTags.filter((tag) =>
      tagToRemove?.email ? tag?.email !== tagToRemove?.email : tag?.id ? tag?.id !== tagToRemove?.id : tag != tagToRemove
    );
    setTags(updatedTags);
    handleRemoveTags(updatedTags);
    setInputError(false);
  };

  const handleBlur = () => {
    addTag();
    const willHaveTags =
      (tag?.length ?? 0) > 0 || (typeof inputValue === "string" && inputValue.trim() !== "");
    if (collapsedWhenHasTags && willHaveTags) {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    const prevTags = prevTagsRef.current;
    const currentTags = tags || [];
    
    // Check if tags were replaced (old tags removed when new ones added)
    if (prevTags.length > 0 && JSON.stringify(prevTags) !== JSON.stringify(currentTags)) {
      const prevTagIds = new Set(
        prevTags.map((t) => 
          typeof t === "string" ? t : t?.id ?? t?.[targetKey] ?? t
        )
      );
      const currentTagIds = new Set(
        currentTags.map((t) => 
          typeof t === "string" ? t : t?.id ?? t?.[targetKey] ?? t
        )
      );
      
      // Find tags that existed before but not now (being replaced/removed)
      const removedTags = prevTags.filter((t) => {
        const id = typeof t === "string" ? t : t?.id ?? t?.[targetKey] ?? t;
        return !currentTagIds.has(id);
      });
      
      // If tags were removed (replaced), highlight them briefly before updating
      if (removedTags.length > 0) {
        const highlightSet = new Set(
          removedTags.map((t) => {
            const id = typeof t === "string" ? t : t?.id ?? t?.[targetKey] ?? t;
            return id;
          })
        );
        setTagsToHighlight(highlightSet);
        
        // Keep old tags visible (highlighted) for a brief moment, then update
        setTimeout(() => {
          setTags(currentTags);
          setTagsToHighlight(new Set());
        }, 300);
        
        // Update ref immediately for next comparison
        prevTagsRef.current = currentTags;
        return;
      }
    }
    
    prevTagsRef.current = currentTags;
    setTags(currentTags);
    if (JSON.stringify(tags) != JSON.stringify(tag)) {
      setInputValue('');
    }
  }, [tags, targetKey]);

  const handleClickOutside = (event) => {
    if (componentRef.current && !componentRef.current.contains(event.target)) {
      const pendingValue = tagInputRef.current ? tagInputRef.current.value : "";
      addTag(pendingValue);
      const shouldCollapse =
        collapsedWhenHasTags &&
        ((tag?.length ?? 0) > 0 || (typeof pendingValue === "string" && pendingValue.trim() !== ""));
      if (shouldCollapse) {
        setIsExpanded(false);
      }
      if (eventAddedRef.current) {
        document.removeEventListener("mousedown", handleClickOutside);
        eventAddedRef.current = false;
      }
    }
  };

  const handleContainerClick = () => {
    if (!eventAddedRef.current) {
      document.addEventListener("mousedown", handleClickOutside);
      eventAddedRef.current = true;
    }
  };

  useEffect(() => {
    if (
      autoFocus &&
      tagInputRef.current &&
      !(limit != null && (tag?.length ?? 0) >= limit)
    ) {
      setIsExpanded(true);
      handleContainerClick();
      tagInputRef.current.focus();
    }
  }, [autoFocus, tag?.length, limit]);

  useEffect(() => {
    return () => {
      if (eventAddedRef.current) {
        document.removeEventListener("mousedown", handleClickOutside);
        eventAddedRef.current = false;
      }
    };
  }, []);

  useEffect(() => {
    if ((tag?.length ?? 0) === 0) {
      setIsExpanded(true);
    }
  }, [tag?.length]);

  useEffect(() => {
    if (!collapsedWhenHasTags) return;
    if (autoFocus) return;

    const hasTags = (tag?.length ?? 0) > 0;
    if (!hasTags) return;

    // If tags arrive asynchronously and the input isn't being actively used,
    // collapse to the CTA state (expected editor behavior).
    const isInputFocused = tagInputRef.current === document.activeElement;
    const hasPendingText = typeof inputValue === "string" && inputValue.trim() !== "";
    if (!isInputFocused && !hasPendingText) {
      setIsExpanded(false);
    }
  }, [tag?.length, collapsedWhenHasTags, autoFocus]); // intentionally exclude inputValue to avoid flicker while typing

  const openInput = () => {
    if (limit != null && (tag?.length ?? 0) >= limit) {
      showNotification({ toastMessage: `You can not add more than ${limit} tags here.` });
      return;
    }
    setIsExpanded(true);
    // Let the input render before focusing
    requestAnimationFrame(() => {
      handleContainerClick();
      tagInputRef.current?.focus?.();
    });
  };

  const renderInput = () => {
    const hasTags = (tag?.length ?? 0) > 0;
    const atLimit = limit != null && (tag?.length ?? 0) >= limit;
    const showTextInput = (!collapsedWhenHasTags || isExpanded || !hasTags) && !atLimit;
    const showCollapsedState = collapsedWhenHasTags && !isExpanded && hasTags;

    if (showCollapsedState) {
      return null;
    }

    return (
      <div
        className={`tags-input-container mb-1 ${hasTags ? "has-tags" : ""}`}
        ref={componentRef}
        onClick={handleContainerClick}
      >
        {showTextInput ? (
          <Form.Control
            type="text"
            ref={tagInputRef}
            placeholder={placeholderText ?? "Add a tag"}
            value={inputValue}
            maxLength={32}
            onChange={(e) => {
              setInputValue(e.target.value);
              setInputError(false);
              triggerKeyDownEvent(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => autoSubmit == false && handleBlur()}
            className={`tags-input ${inputError == true ? "tag-error" : ""}`}
          />
        ) : null}
      </div>
    );
  };

  const renderTags = () => {
    const getTagId = (elem) => {
      if (typeof elem === "string") return elem;
      return elem?.id ?? elem?.[targetKey] ?? elem;
    };
    
    return (
      <div className="tags-display">
        {tag?.map((elem, index) => {
          const tagId = getTagId(elem);
          const isHighlighted = highlightedIndex === index || tagsToHighlight.has(tagId);
          const tagText = elem[`${targetKey}`] ?? elem;
          const tagLabel = typeof tagText === "string" ? tagText : tagText?.name ?? tagText;
          
          if (useTagPrimaryStyle) {
            // Use tag-primary style for KDF listing page - use me-1 (margin-end) instead of mx-1 to avoid gaps
            return (
              <span
                key={index}
                className={`tag-primary text-white me-1 ${isHighlighted ? "highlight" : ""}`}
              >
                {tagLabel}
                <button
                  type="button"
                  className="tag-remove-btn"
                  aria-label={`Remove tag ${tagLabel}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    removeTag(elem);
                  }}
                >
                  ×
                </button>
              </span>
            );
          }
          
          // Default Badge style for other places
          return (
            <Badge key={index} className={isHighlighted ? "highlight" : ""}>
              <span>{tagLabel}</span>
              <button
                type="button"
                className="tag-remove-btn"
                aria-label={`Remove ${tagLabel}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  removeTag(elem);
                }}
              >
                ×
              </button>
            </Badge>
          );
        })}
      {collapsedWhenHasTags &&
        (tag?.length ?? 0) > 0 &&
        !isExpanded && (
          <span
            className={`tags-add-cta ${
              limit != null && (tag?.length ?? 0) >= limit ? "disabled" : ""
            }`}
            role="button"
            tabIndex={0}
            aria-label="Add tag"
            aria-disabled={limit != null && (tag?.length ?? 0) >= limit}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openInput();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                openInput();
              }
            }}
          >
            <img src="/icons/add_2.svg" width="14" alt="" />
          </span>
        )}
      </div>
    );
  };

  const wrapperClasses = ["custom-tags-wrapper"];
  if (tagsPosition === "above") {
    wrapperClasses.push("tags-wrapper-above");
  }
  if (wrapperClassName) {
    wrapperClasses.push(wrapperClassName);
  }

  return (
    <div className={wrapperClasses.join(" ")} >
      {tagsPosition === "above" && renderTags()}
      {renderInput()}
      {tagsPosition !== "above" && renderTags()}
    </div>
  );
};

export default CustomTagsInput;
